import { motion } from 'framer-motion';
import { Mail, MessageSquare } from 'lucide-react';
import { useState } from 'react';
import SEOHead from '../components/SEOHead';
import { trackFormSubmit } from '../utils/analytics';

const HS_PORTAL_ID = process.env.REACT_APP_HS_PORTAL_ID;
const HS_FORM_GUID = process.env.REACT_APP_HS_FORM_GUID;

const submitToHubSpot = async ({ name, email, company, message }) => {
  if (!HS_PORTAL_ID || !HS_FORM_GUID) return;
  const [firstname, ...rest] = name.trim().split(' ');
  await fetch(
    `https://api.hsforms.com/submissions/v3/integration/submit/${HS_PORTAL_ID}/${HS_FORM_GUID}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fields: [
          { name: 'firstname', value: firstname },
          { name: 'lastname', value: rest.join(' ') || '' },
          { name: 'email', value: email },
          { name: 'company', value: company || '' },
          { name: 'message', value: message },
        ],
        context: { pageUri: window.location.href, pageName: document.title },
      }),
    }
  );
};

const Contact = () => {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const form = e.currentTarget;
    const body = {
      name: form.name.value,
      email: form.email.value,
      company: form.company.value || undefined,
      message: form.message.value,
    };
    try {
      await submitToHubSpot(body);
      trackFormSubmit('contact', { company: body.company });
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please email us directly at sales@drivor.in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Contact"
        description="Contact Drivor sales for driver hire, cab hire, airport transfer, corporate travel, office commute, outstation, and long-term driver requirements."
        url="https://drivor.in/contact"
      />

      <div className="border-b border-slate-200 bg-white">
        <div className="container-custom py-16 lg:py-20">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-4xl font-bold tracking-tight text-slate-900">
              Talk to Drivor sales
            </h1>
            <p className="mt-4 text-lg text-slate-600">
              Share your driver or cab requirement and we will help you plan the right booking workflow.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-surface-page py-16">
        <div className="container-custom">
          <div className="mx-auto grid max-w-5xl gap-12 lg:grid-cols-5">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="lg:col-span-2"
            >
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                  Direct
                </h2>
                <a
                  href="mailto:sales@drivor.in"
                  className="mt-4 flex items-center gap-3 text-slate-900 hover:text-primary-700"
                >
                  <Mail className="h-5 w-5 text-primary-600" aria-hidden />
                  <span className="font-medium">sales@drivor.in</span>
                </a>
              </div>
              <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-6">
                <MessageSquare className="h-8 w-8 text-primary-600" aria-hidden />
                <p className="mt-3 text-sm leading-relaxed text-slate-600">
                  Tell us the city, service type, schedule, and whether you need a one-time booking,
                  recurring office commute, corporate driver, permanent duty driver, airport transfer,
                  or outstation plan.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="lg:col-span-3"
            >
              {submitted ? (
                <div className="rounded-2xl border border-primary-200 bg-primary-50 p-8 text-center shadow-card">
                  <p className="font-semibold text-primary-900">
                    Message sent &mdash; we&apos;ll be in touch soon.
                  </p>
                  <p className="mt-2 text-sm text-primary-800">
                    Expect a reply from sales@drivor.in within one business day.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="rounded-2xl border border-slate-200 bg-white p-8 shadow-card"
                >
                  <div className="space-y-5">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-slate-700">
                        Name
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        autoComplete="name"
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-slate-700">
                        Work email
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        autoComplete="email"
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-slate-700">
                        Organization
                      </label>
                      <input
                        id="company"
                        name="company"
                        type="text"
                        autoComplete="organization"
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-slate-700">
                        How can we help?
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                        placeholder="Tell us what you need: driver hire, cab hire, airport transfer, office pickup and drop, outstation travel, or corporate mobility."
                      />
                    </div>
                  </div>
                  {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary mt-6 w-full sm:w-auto disabled:opacity-60"
                  >
                    {loading ? 'Sending…' : 'Send message'}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;
